import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnChanges,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
  afterNextRender,
  inject,
  Injector,
  runInInjectionContext,
} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { Spinner } from '../spinner/spinner';

@Component({
  selector: 'app-timesheet-submission-required-popup',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule, Spinner],
  templateUrl: './timesheet-submission-required-popup.html',
  styleUrls: ['./timesheet-submission-required-popup.scss'],
})
export class TimesheetSubmissionRequiredPopup implements AfterViewInit, OnChanges, OnDestroy {
  private readonly document = inject(DOCUMENT);
  private readonly renderer = inject(Renderer2);
  private readonly injector = inject(Injector);

  /** Move overlay under document.body so parent overflow cannot clip it. */
  @Input() appendToBody = true;
  @Input() show = false;
  @Input() title = 'Timesheet submission required';
  @Input() message = 'Please submit your last week timesheet.';
  /** Filled hours from get-timesheet-status (`totalHours`). */
  @Input() totalHours: number | null = null;
  @Input() hoursLoading = false;
  @Input() confirmText = 'Go to Timesheet';

  @Output() confirm = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  @ViewChild('bodyRoot', { read: ElementRef })
  private bodyRoot?: ElementRef<HTMLElement>;

  formattedHours(): string {
    const hours = Number(this.totalHours);
    if (!Number.isFinite(hours)) {
      return '0';
    }
    if (Number.isInteger(hours)) {
      return String(hours);
    }
    return hours.toFixed(2).replace(/\.?0+$/, '');
  }

  ngAfterViewInit(): void {
    if (this.appendToBody) {
      this.scheduleMoveOverlayToBody();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['appendToBody'] && this.appendToBody) {
      this.scheduleMoveOverlayToBody();
    }
  }

  ngOnDestroy(): void {
    this.removeOverlayFromBody();
  }

  onConfirm(): void {
    this.confirm.emit();
  }

  private scheduleMoveOverlayToBody(): void {
    runInInjectionContext(this.injector, () => {
      afterNextRender(() => this.moveOverlayToBody());
    });
  }

  private moveOverlayToBody(): void {
    if (!this.appendToBody) {
      return;
    }
    const root = this.bodyRoot?.nativeElement;
    if (!root || root.parentNode === this.document.body) {
      return;
    }
    this.renderer.appendChild(this.document.body, root);
  }

  private removeOverlayFromBody(): void {
    const root = this.bodyRoot?.nativeElement;
    if (root && root.parentNode === this.document.body) {
      this.renderer.removeChild(this.document.body, root);
    }
  }
}
